import { useState, type FormEvent } from 'react';
import { useAuthStore } from '../../auth/useAuthStore';
import { UrbanNetworkLoaderV2 } from '../../layouts/components/UrbanNetworkLoaderV2';
import './GuestLoginPanel.css';

interface GuestLoginPanelProps {
  projectName: string;
  territory: string | null;
}

// ─── Bloque de marca Westeer ──────────────────────────────────────────────────

function PanelBrand(): JSX.Element {
  return (
    <div className="glp-brand-block" aria-hidden="true">
      <div className="glp-logo">
        <UrbanNetworkLoaderV2 size="xs" />
        <span className="glp-logo-wordmark">WAYLINE</span>
      </div>
      <p className="glp-brand-statement">Territorio. Movilidad. Decisión.</p>
    </div>
  );
}

// ─── Bloque de identidad del proyecto ────────────────────────────────────────

function PanelIdentity({
  territory,
  projectName,
}: {
  territory: string | null;
  projectName: string;
}): JSX.Element {
  return (
    <div className="glp-identity">
      {territory && (
        <div className="glp-territory">
          <span className="glp-territory-dot" aria-hidden="true" />
          {territory}
        </div>
      )}
      <span className="glp-project-label">Proyecto</span>
      <h1 className="glp-project-name">{projectName}</h1>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function GuestLoginPanel({ projectName, territory }: GuestLoginPanelProps): JSX.Element {
  const { login, isLoading, error, clearError } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    clearError();
    void (async (): Promise<void> => {
      try {
        await login({ email, password });
      } catch {
        // Error ya manejado por el store
      }
    })();
  };

  return (
    <div className="glp-panel" role="main" aria-label={`Acceso al proyecto ${projectName}`}>
      {/* Marca de agua — W grande, detrás del contenido */}
      <img
        src="/images/wayline-logo.svg"
        alt=""
        aria-hidden="true"
        className="glp-watermark"
      />

      {/* Bloque de marca */}
      <PanelBrand />

      {/* Bloque de proyecto */}
      <PanelIdentity territory={territory} projectName={projectName} />

      {/* Formulario */}
      <form className="glp-form" onSubmit={handleSubmit} noValidate>
        <input
          type="email"
          className="glp-input"
          placeholder="Correo electrónico"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          disabled={isLoading}
          autoComplete="email"
          aria-label="Correo electrónico"
        />
        <input
          type="password"
          className="glp-input"
          placeholder="Contraseña"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          disabled={isLoading}
          autoComplete="current-password"
          aria-label="Contraseña"
        />

        {error && (
          <div className="glp-error" role="alert" aria-live="polite">
            {error}
          </div>
        )}

        <button
          type="submit"
          className={`glp-submit${isLoading ? ' glp-submit--loading' : ''}`}
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? 'Ingresando…' : 'Ingresar al proyecto'}
        </button>
      </form>

      {/* Recuperar acceso */}
      <div className="glp-recover">
        <a href="mailto:soporte@wayline.city" className="glp-recover-link">
          Recuperar acceso
        </a>
      </div>
    </div>
  );
}
